import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import type { CollectionPoint, GrowthPoint } from '@/types';
import { formatCurrency } from '@/lib/payments';
import { ChartLegend, ChartTooltipBox, EmptyText } from './SectionCard';

// Recharts needs a fixed-size box; this lets the chart fill the card.
const FILL_CHART_CLASS =
  'relative w-full flex-1 !aspect-auto !justify-start [&_.recharts-responsive-container]:!absolute [&_.recharts-responsive-container]:!inset-0 [&_.recharts-responsive-container]:!h-full [&_.recharts-responsive-container]:!w-full';
const AXIS_CLASS = 'text-xs fill-gray-500';

const COLLECTION_SERIES = [
  { key: 'policyExpected', label: 'Policy Target', color: '#e5e7eb' },
  { key: 'policy', label: 'Policy Received', color: '#8fa9cf' },
  { key: 'loan', label: 'Loan Repaid', color: '#7fc8bf' },
] as const;

const collectionConfig = Object.fromEntries(
  COLLECTION_SERIES.map((series) => [series.key, { label: series.label, color: series.color }])
) satisfies ChartConfig;

/** Policy target vs policy received vs loan repaid, per month. */
export function MonthlyCollectionChart({ data }: { data: CollectionPoint[] }) {
  if (data.length === 0) return <EmptyText>No collection data yet</EmptyText>;

  return (
    <>
      <ChartContainer config={collectionConfig} className={`min-h-[280px] ${FILL_CHART_CLASS}`}>
        <BarChart data={data} margin={{ top: 4, right: 12, left: 8, bottom: 4 }} barGap={3} barCategoryGap="14%">
          <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-gray-200" />
          <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={6} interval={0} className={AXIS_CLASS} />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={4}
            width={56}
            className={AXIS_CLASS}
            tickFormatter={(value) => `₹${value / 1000}k`}
          />
          <ChartTooltip
            cursor={{ fill: 'rgba(67, 105, 160, 0.06)' }}
            content={({ active, payload }) => {
              const point = payload?.[0]?.payload as CollectionPoint | undefined;
              if (!active || !point) return null;
              return (
                <ChartTooltipBox
                  title={point.label}
                  rows={COLLECTION_SERIES.map((series) => ({ ...series, value: formatCurrency(point[series.key]) }))}
                />
              );
            }}
          />
          {COLLECTION_SERIES.map((series) => (
            <Bar key={series.key} dataKey={series.key} fill={series.color} radius={[4, 4, 0, 0]} />
          ))}
        </BarChart>
      </ChartContainer>
      <ChartLegend items={[...COLLECTION_SERIES]} />
    </>
  );
}

/** Running total and new items per month (members or policies). */
export function GrowthChart({ data, totalLabel, addedLabel }: { data: GrowthPoint[]; totalLabel: string; addedLabel: string }) {
  if (data.length === 0) return <EmptyText>No data yet</EmptyText>;

  const series = [
    { key: 'total', label: totalLabel, color: '#4369a0' },
    { key: 'added', label: addedLabel, color: '#0d9488' },
  ] as const;
  const config = {
    total: { label: totalLabel, color: '#4369a0' },
    added: { label: addedLabel, color: '#0d9488' },
  } satisfies ChartConfig;

  return (
    <>
      <ChartContainer config={config} className={`min-h-[240px] ${FILL_CHART_CLASS}`}>
        <AreaChart data={data} margin={{ top: 4, right: 12, left: 8, bottom: 4 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-gray-200" />
          <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={6} interval={0} className={AXIS_CLASS} />
          <YAxis tickLine={false} axisLine={false} tickMargin={4} width={32} className={AXIS_CLASS} />
          <ChartTooltip
            content={({ active, payload }) => {
              const point = payload?.[0]?.payload as GrowthPoint | undefined;
              if (!active || !point) return null;
              return (
                <ChartTooltipBox
                  title={point.label}
                  rows={series.map((item) => ({ ...item, value: String(point[item.key]) }))}
                />
              );
            }}
          />
          {series.map((item) => (
            <Area
              key={item.key}
              type="monotone"
              dataKey={item.key}
              stroke={item.color}
              fill={item.color}
              fillOpacity={item.key === 'total' ? 0.15 : 0.1}
              strokeWidth={2}
            />
          ))}
        </AreaChart>
      </ChartContainer>
      <ChartLegend items={[...series]} />
    </>
  );
}

export interface DonutItem {
  key: string;
  label: string;
  value: number;
  color: string;
  /** Tailwind classes for the small number box under the chart. */
  boxClass: string;
}

/** Donut chart with a number box for each slice. */
export function DonutChart({
  items,
  formatValue = String,
  emptyText,
}: {
  items: DonutItem[];
  formatValue?: (value: number) => string;
  emptyText: string;
}) {
  const slices = items.filter((item) => item.value > 0);
  if (slices.length === 0) return <EmptyText>{emptyText}</EmptyText>;

  const config = Object.fromEntries(items.map((item) => [item.key, { label: item.label, color: item.color }])) satisfies ChartConfig;

  return (
    <div className="flex flex-col items-center gap-4">
      <ChartContainer config={config} className="mx-auto h-[200px] w-full max-w-[220px]">
        <PieChart>
          <ChartTooltip content={<ChartTooltipContent hideLabel formatter={(value) => formatValue(Number(value))} />} />
          <Pie data={slices} dataKey="value" nameKey="key" innerRadius={52} outerRadius={78} strokeWidth={2}>
            {slices.map((item) => (
              <Cell key={item.key} fill={item.color} />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>
      <div className="grid w-full gap-2 text-center" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
        {items.map((item) => (
          <div key={item.key} className={`rounded-lg px-2 py-2 ${item.boxClass}`}>
            <p className="text-base font-semibold tabular-nums">{formatValue(item.value)}</p>
            <p className="text-xs text-gray-500">{item.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
